import { useEffect, useRef, useState } from 'react';

import { useServiceFeatures } from 'hooks/useServiceContext';
import { getCommodities } from 'lib/api/getCommodities';
import { TMSCommodity } from 'types/Load';
import { Maybe } from 'types/UtilityTypes';
import { TMS } from 'types/enums/Integrations';

import { useQuickQuoteContext } from '../../context/QuickQuoteContext';

/**
 * Fetches Turvo commodities for the selected LTL customer.
 */
export function useTurvoCommodities(): {
  commodities: Maybe<TMSCommodity[]>;
  isLoading: boolean;
  hasCustomer: boolean;
  hasTmsId: boolean;
  hasCustomerId: boolean;
} {
  const { ltlCustomer } = useQuickQuoteContext();
  const { ltlTMSIntegrations } = useServiceFeatures();
  const [commodities, setCommodities] = useState<Maybe<TMSCommodity[]>>([]);
  const [isLoading, setIsLoading] = useState(false);
  const requestIdRef = useRef(0);

  const turvoTmsId = ltlTMSIntegrations?.find((i) => i.name === TMS.Turvo)?.id;
  const customerExternalID = ltlCustomer?.externalId ?? ltlCustomer?.customerId;

  // Load customer commodities from shared /commodities endpoint.
  useEffect(() => {
    const requestId = ++requestIdRef.current;

    if (!turvoTmsId || !customerExternalID) {
      setCommodities([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    const timeoutId = window.setTimeout(async () => {
      const res = await getCommodities(
        turvoTmsId,
        false,
        false,
        undefined,
        undefined,
        customerExternalID
      );

      if (requestId !== requestIdRef.current) {
        return;
      }

      if (res.isOk()) {
        setCommodities(res.value.commodityList ?? []);
      } else {
        setCommodities([]);
      }

      setIsLoading(false);
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [customerExternalID, turvoTmsId]);

  return {
    commodities,
    isLoading,
    hasCustomer: Boolean(ltlCustomer),
    hasTmsId: Boolean(turvoTmsId),
    hasCustomerId: Boolean(customerExternalID),
  };
}
